import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { VbaShowcaseContent } from "../data/content";
import "./VbaConfiguratorShowcase.scss";

gsap.registerPlugin(ScrollTrigger);

type Props = {
  config: VbaShowcaseContent;
};

export default function VbaConfiguratorShowcase({ config }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLLIElement>(null);
  const step3Ref = useRef<HTMLLIElement>(null);
  const docFillTween = useRef<gsap.core.Tween | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      root.querySelectorAll<SVGElement>(".vba-showcase__flow-path").forEach((path) => {
        path.style.strokeDashoffset = "0";
      });
      root.querySelectorAll<HTMLElement>(".vba-showcase__doc-fill--animate").forEach((el) => {
        el.style.transform = "scaleX(1)";
        el.style.opacity = "1";
      });
      return;
    }

    const playDocFill = () => {
      docFillTween.current?.kill();
      docFillTween.current = gsap.fromTo(
        root.querySelectorAll(".vba-showcase__doc-fill--animate"),
        { scaleX: 0, opacity: 0.35 },
        {
          scaleX: 1,
          opacity: 1,
          duration: 0.55,
          stagger: 0.07,
          ease: "power2.out",
          transformOrigin: "left center",
        }
      );
    };

    const step3 = step3Ref.current;
    const onStep3Hover = () => playDocFill();

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".vba-showcase__step").forEach((step) => {
        gsap.from(step, {
          opacity: 0,
          y: 36,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: step,
            start: "top 88%",
            toggleActions: "play none none reverse",
          },
        });
      });

      root.querySelectorAll<SVGPathElement>(".vba-showcase__flow-path").forEach((path) => {
        const length = path.getTotalLength();
        gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
        gsap.to(path, {
          strokeDashoffset: 0,
          duration: 1.1,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: step2Ref.current,
            start: "top 82%",
            toggleActions: "play none none reverse",
          },
        });
      });

      gsap.from(".vba-showcase__engine-core", {
        scale: 0.6,
        opacity: 0,
        duration: 0.6,
        ease: "back.out(1.4)",
        scrollTrigger: {
          trigger: step2Ref.current,
          start: "top 82%",
          toggleActions: "play none none reverse",
        },
      });

      ScrollTrigger.create({
        trigger: step3Ref.current,
        start: "top 85%",
        onEnter: playDocFill,
        onEnterBack: playDocFill,
      });
    }, root);

    step3?.addEventListener("mouseenter", onStep3Hover);

    return () => {
      step3?.removeEventListener("mouseenter", onStep3Hover);
      docFillTween.current?.kill();
      ctx.revert();
    };
  }, [config]);

  const { ui, steps, intro } = config;

  return (
    <div className="vba-showcase" ref={rootRef}>
      <p className="vba-showcase__intro">{intro}</p>
      <ol className="vba-showcase__steps">
        <li className="vba-showcase__step vba-showcase__step--1">
          <div className="vba-showcase__step-head">
            <h3 className="vba-showcase__step-title">{steps[0].title}</h3>
          </div>
          <div className="vba-showcase__panel vba-showcase__panel--form" aria-hidden="true">
            <label className="vba-showcase__field">
              <span>{ui.deviceType}</span>
              <select disabled defaultValue="vacuum">
                <option value="vacuum">{ui.deviceOption1}</option>
                <option value="crane">{ui.deviceOption2}</option>
              </select>
            </label>
            <label className="vba-showcase__field">
              <span>{ui.capacity}</span>
              <select disabled defaultValue="500">
                <option value="500">{ui.capacityOption1}</option>
                <option value="1000">{ui.capacityOption2}</option>
              </select>
            </label>
            <fieldset className="vba-showcase__field vba-showcase__field--radio">
              <legend>{ui.control}</legend>
              <label>
                <input type="radio" name="vba-control-demo" checked readOnly disabled />
                {ui.controlManual}
              </label>
              <label className="vba-showcase__option-locked">
                <input type="radio" name="vba-control-demo" disabled />
                <span className="vba-showcase__option-label">{ui.controlElectric}</span>
                <span className="vba-showcase__lock-tag">{ui.lockedHint}</span>
              </label>
            </fieldset>
          </div>
          <p className="vba-showcase__caption">{steps[0].caption}</p>
        </li>

        <li className="vba-showcase__step vba-showcase__step--2" ref={step2Ref}>
          <div className="vba-showcase__step-head">
            <h3 className="vba-showcase__step-title">{steps[1].title}</h3>
          </div>
          <div className="vba-showcase__panel vba-showcase__panel--flow" aria-hidden="true">
            <svg className="vba-showcase__flow-svg" viewBox="0 0 420 120" role="img">
              <rect className="vba-showcase__flow-box" x="8" y="34" width="96" height="52" rx="6" />
              <text x="56" y="64" textAnchor="middle" className="vba-showcase__flow-label">
                {ui.flowParams}
              </text>
              <path
                className="vba-showcase__flow-path"
                d="M 108 60 H 158"
                fill="none"
                strokeWidth="2"
              />
              <circle className="vba-showcase__engine-core" cx="210" cy="60" r="34" />
              <text x="210" y="65" textAnchor="middle" className="vba-showcase__flow-label vba-showcase__flow-label--engine">
                {ui.flowEngine}
              </text>
              <path
                className="vba-showcase__flow-path"
                d="M 246 60 H 296"
                fill="none"
                strokeWidth="2"
              />
              <rect className="vba-showcase__flow-box" x="302" y="18" width="110" height="36" rx="6" />
              <text x="357" y="40" textAnchor="middle" className="vba-showcase__flow-label">
                {ui.flowPrice}
              </text>
              <rect className="vba-showcase__flow-box vba-showcase__flow-box--muted" x="302" y="66" width="110" height="36" rx="6" />
              <text x="357" y="88" textAnchor="middle" className="vba-showcase__flow-label">
                {ui.flowLocks}
              </text>
              <text x="357" y="112" textAnchor="middle" className="vba-showcase__flow-sublabel">
                {ui.flowOutput}
              </text>
            </svg>
          </div>
          <p className="vba-showcase__caption">{steps[1].caption}</p>
        </li>

        <li className="vba-showcase__step vba-showcase__step--3" ref={step3Ref}>
          <div className="vba-showcase__step-head">
            <h3 className="vba-showcase__step-title">{steps[2].title}</h3>
          </div>
          <div className="vba-showcase__panel vba-showcase__panel--doc" aria-hidden="true">
            <div className="vba-showcase__doc">
              <div className="vba-showcase__doc-fill vba-showcase__doc-line vba-showcase__doc-line--title">
                {ui.docHeader}
              </div>
              <div className="vba-showcase__doc-fill vba-showcase__doc-fill--animate vba-showcase__doc-line" />
              <div className="vba-showcase__doc-fill vba-showcase__doc-fill--animate vba-showcase__doc-line vba-showcase__doc-line--short" />
              <div className="vba-showcase__doc-fill vba-showcase__doc-fill--animate vba-showcase__doc-table">
                <span>{ui.docTable}</span>
                <div className="vba-showcase__doc-row" />
                <div className="vba-showcase__doc-row" />
                <div className="vba-showcase__doc-row" />
              </div>
              <div className="vba-showcase__doc-fill vba-showcase__doc-fill--animate vba-showcase__doc-image">
                {ui.docImage}
              </div>
              <p className="vba-showcase__doc-total">{ui.docTotal}</p>
            </div>
          </div>
          <p className="vba-showcase__caption">{steps[2].caption}</p>
        </li>
      </ol>
    </div>
  );
}
